/**
 * Publish Slide Modal
 *
 * Modal for publishing slides and managing share links.
 * Features:
 * - Publish button to generate a share link
 * - Share URL with copy button
 * - View count display
 * - Unpublish button to revoke access
 */

import React, { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Globe, Copy, Check, Trash2, ExternalLink, Eye, Loader2, Share2 } from 'lucide-react';
import { slidesAPI } from '../../services/api';

interface ShareLinkInfo {
  id: string;
  shareToken: string;
  shareUrl: string;
  isActive: boolean;
  expiresAt: string | null;
  viewCount: number;
  lastViewedAt: string | null;
  createdAt: string;
  createdBy: string;
  revokedAt: string | null;
}

interface PublishStatusResponse {
  isPublished: boolean;
  shareLinks: ShareLinkInfo[];
}

interface PublishSlideModalProps {
  isOpen: boolean;
  onClose: () => void;
  conversationId: string;
  slideId: string;
  slideTitle?: string;
  publishStatus: PublishStatusResponse | null;
  onPublishStatusChange: (status: PublishStatusResponse) => void;
}

export const PublishSlideModal: React.FC<PublishSlideModalProps> = ({
  isOpen,
  onClose,
  conversationId,
  slideId,
  slideTitle,
  publishStatus,
  onPublishStatusChange,
}) => {
  const [isPublishing, setIsPublishing] = useState(false);
  const [isUnpublishing, setIsUnpublishing] = useState<string | null>(null);
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const activeLinks = publishStatus?.shareLinks.filter(link => link.isActive) || [];
  const isPublished = activeLinks.length > 0;

  // Handle publish
  const handlePublish = useCallback(async () => {
    setIsPublishing(true);
    setError(null);

    try {
      const result = await slidesAPI.publish(conversationId, slideId);

      // Update publish status
      const newStatus: PublishStatusResponse = {
        isPublished: true,
        shareLinks: [...(publishStatus?.shareLinks || []), result.shareLink],
      };
      onPublishStatusChange(newStatus);

      // Show success toast
      window.dispatchEvent(new CustomEvent('showToast', {
        detail: {
          message: 'Slide published! Share link created.',
          type: 'success'
        }
      }));
    } catch (err: any) {
      console.error('Failed to publish slide:', err);
      setError(err.response?.data?.message || 'Failed to publish slide');
    } finally {
      setIsPublishing(false);
    }
  }, [conversationId, slideId, publishStatus, onPublishStatusChange]);

  // Handle unpublish (revoke)
  const handleUnpublish = useCallback(async (shareLinkId: string) => {
    setIsUnpublishing(shareLinkId);
    setError(null);

    try {
      await slidesAPI.unpublish(conversationId, slideId, shareLinkId);

      // Update publish status - mark the link as inactive
      const updatedLinks = publishStatus?.shareLinks.map(link =>
        link.id === shareLinkId
          ? { ...link, isActive: false, revokedAt: new Date().toISOString() }
          : link
      ) || [];

      const newStatus: PublishStatusResponse = {
        isPublished: updatedLinks.some(link => link.isActive),
        shareLinks: updatedLinks,
      };
      onPublishStatusChange(newStatus);

      // Show success toast
      window.dispatchEvent(new CustomEvent('showToast', {
        detail: {
          message: 'Share link revoked',
          type: 'success'
        }
      }));
    } catch (err: any) {
      console.error('Failed to unpublish slide:', err);
      setError(err.response?.data?.message || 'Failed to revoke share link');
    } finally {
      setIsUnpublishing(null);
    }
  }, [conversationId, slideId, publishStatus, onPublishStatusChange]);

  // Copy link to clipboard
  const handleCopyLink = useCallback(async (shareUrl: string, linkId: string) => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopiedLinkId(linkId);
      setTimeout(() => setCopiedLinkId(null), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  }, []);

  // Open link in new tab
  const handleOpenLink = useCallback((shareUrl: string) => {
    window.open(shareUrl, '_blank');
  }, []);

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isPublished ? (
              <Globe className="h-5 w-5 text-green-500" />
            ) : (
              <Share2 className="h-5 w-5" />
            )}
            {isPublished ? 'Manage Share Link' : 'Publish Slide'}
          </DialogTitle>
          <DialogDescription>
            {slideTitle ? `"${slideTitle}"` : 'Share this slide'}
            {isPublished
              ? ' - Anyone with the link can view this slide.'
              : ' - Create a public link to share with anyone.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {error && (
            <div className="bg-destructive/10 text-destructive rounded-lg p-3 text-sm">
              {error}
            </div>
          )}

          {!isPublished ? (
            // Not published - show publish button
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <div className="p-4 rounded-full bg-muted">
                <Share2 className="h-8 w-8 text-muted-foreground" />
              </div>
              <p className="text-sm text-muted-foreground text-center max-w-xs">
                Publishing this slide will create a public link that anyone can use to view it.
              </p>
              <Button
                onClick={handlePublish}
                disabled={isPublishing}
                size="lg"
                className="gap-2"
              >
                {isPublishing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Publishing...
                  </>
                ) : (
                  <>
                    <Globe className="h-4 w-4" />
                    Publish Slide
                  </>
                )}
              </Button>
            </div>
          ) : (
            // Published - show active links
            <div className="space-y-4">
              {activeLinks.map((link) => (
                <div
                  key={link.id}
                  className="border rounded-lg p-4 space-y-3"
                >
                  {/* Share URL */}
                  <div className="flex items-center gap-2">
                    <Input
                      value={link.shareUrl}
                      readOnly
                      className="flex-1 bg-muted font-mono text-sm"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleCopyLink(link.shareUrl, link.id)}
                      title="Copy link"
                    >
                      {copiedLinkId === link.id ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleOpenLink(link.shareUrl)}
                      title="Open in new tab"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Stats and actions */}
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-4 text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Eye className="h-4 w-4" />
                        {link.viewCount} views
                      </span>
                      <span>
                        Created {formatDate(link.createdAt)}
                      </span>
                    </div>

                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => handleUnpublish(link.id)}
                      disabled={isUnpublishing === link.id}
                    >
                      {isUnpublishing === link.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Trash2 className="h-4 w-4 mr-1" />
                          Revoke
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ))}

              {/* Option to create additional link */}
              <Button
                variant="outline"
                onClick={handlePublish}
                disabled={isPublishing}
                className="w-full gap-2"
              >
                {isPublishing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Share2 className="h-4 w-4" />
                    Create Another Link
                  </>
                )}
              </Button>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
